import {
  ReactNode,
  useRef,
  memo,
  useLayoutEffect,
  useMemo,
  useCallback,
} from 'react';
import { Stack } from 'react-bootstrap';
import { IWorkerData } from '@th-types/worker.type';
import { IChatMember } from '@th-types/person.type';
import ChatHeader from '@components/Chat/ChatHeader/ChatHeader';
import ChatBody from '@components/Chat/ChatBody/ChatBody';
import ChatInput from '@components/Chat/ChatInput/ChatInput';
import { ThLoading } from '@components/elements';
import { CombinedMessage } from '@th-types/message.type';
import { UseMutationResult } from '@tanstack/react-query';
import { useUserContext } from '@state/userContext';

const LOAD_OLDER_SCROLL_THRESHOLD_PX = 80;

interface GenericChatProps {
  showWorkerInfo: boolean;
  worker: IWorkerData;
  conversationId: number | null;
  isDirectMessage?: boolean;
  isAnnouncement?: boolean;
  isLoading: boolean;
  error: Error | null;
  messages: CombinedMessage[];
  chatMembers: IChatMember[];
  onViewProfile?: () => void;
  resendMessageMutation: UseMutationResult<
    any,
    Error,
    { message: string; messageId: number },
    unknown
  >;
  onSubmitMessage: (message: string) => Promise<void>;
  isSendingMessage: boolean;
  inputDisabledForMember?: boolean;
  onClose: () => void;
  policyNotice?: ReactNode;
  firstUnreadMessageId?: number | null;
  hasMoreOlder?: boolean;
  isLoadingOlder?: boolean;
  onLoadOlder?: () => void;
}

const arePropsEqual = (
  prevProps: GenericChatProps,
  nextProps: GenericChatProps
) => {
  return (
    prevProps.showWorkerInfo === nextProps.showWorkerInfo &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.isSendingMessage === nextProps.isSendingMessage &&
    prevProps.inputDisabledForMember === nextProps.inputDisabledForMember &&
    prevProps.isDirectMessage === nextProps.isDirectMessage &&
    prevProps.conversationId === nextProps.conversationId &&
    prevProps.isAnnouncement === nextProps.isAnnouncement &&
    prevProps.worker.id === nextProps.worker.id &&
    prevProps.worker.customUrl === nextProps.worker.customUrl &&
    // Both callers memoize this array, so identity tracks content: comparing
    // only the length would skip refetches that replace messages in place.
    prevProps.messages === nextProps.messages &&
    prevProps.chatMembers.length === nextProps.chatMembers.length &&
    prevProps.error?.message === nextProps.error?.message &&
    prevProps.policyNotice === nextProps.policyNotice &&
    prevProps.firstUnreadMessageId === nextProps.firstUnreadMessageId &&
    prevProps.hasMoreOlder === nextProps.hasMoreOlder &&
    prevProps.isLoadingOlder === nextProps.isLoadingOlder
  );
};

const GenericChat = memo(
  ({
    showWorkerInfo,
    worker,
    conversationId,
    isDirectMessage,
    isAnnouncement,
    isLoading,
    error,
    messages,
    chatMembers,
    onViewProfile,
    resendMessageMutation,
    onSubmitMessage,
    isSendingMessage,
    inputDisabledForMember,
    onClose,
    policyNotice,
    firstUnreadMessageId = null,
    hasMoreOlder = false,
    isLoadingOlder = false,
    onLoadOlder,
  }: GenericChatProps) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const chatBodyRef = useRef<HTMLDivElement>(null);
    const stickToBottomRef = useRef(true);
    const pendingScrollRestoreRef = useRef<number | null>(null);
    const initialScrollDoneRef = useRef(false);
    const lastConversationIdRef = useRef<number | null>(null);
    const unreadSeekCountRef = useRef<number | null>(null);
    const { id: userId } = useUserContext();

    const headerWorker = useMemo(() => {
      if (isDirectMessage && chatMembers.length > 0) {
        const otherMember = chatMembers.find((member) => member.id !== userId);
        return otherMember || worker;
      }
      return worker;
    }, [isDirectMessage, chatMembers, userId, worker]);

    /**
     * Capture the scroll height before requesting the page: the top loader
     * mounts as soon as the fetch starts, so measuring afterwards would leave
     * the restore below off by the loader's height.
     */
    const requestOlderMessages = useCallback(() => {
      const body = chatBodyRef.current;
      if (
        !body ||
        !onLoadOlder ||
        !hasMoreOlder ||
        isLoadingOlder ||
        isLoading
      ) {
        return;
      }
      pendingScrollRestoreRef.current = body.scrollHeight;
      stickToBottomRef.current = false;
      onLoadOlder();
    }, [hasMoreOlder, isLoadingOlder, isLoading, onLoadOlder]);

    useLayoutEffect(() => {
      if (conversationId !== lastConversationIdRef.current) {
        lastConversationIdRef.current = conversationId;
        initialScrollDoneRef.current = false;
        stickToBottomRef.current = true;
        unreadSeekCountRef.current = null;
      }

      const body = chatBodyRef.current;
      if (!body || isLoading || messages.length === 0) return;

      const focusInput = () => {
        if (
          inputRef.current &&
          !isSendingMessage &&
          !inputDisabledForMember &&
          !isAnnouncement
        ) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(
            inputRef.current.value.length,
            inputRef.current.value.length
          );
        }
      };

      if (!initialScrollDoneRef.current) {
        if (firstUnreadMessageId != null) {
          const divider = body.querySelector('[data-unread-divider]');
          if (divider) {
            initialScrollDoneRef.current = true;
            pendingScrollRestoreRef.current = null;
            divider.scrollIntoView({ block: 'start' });
            requestAnimationFrame(focusInput);
            return;
          }
          if (messages.some((message) => message.id === firstUnreadMessageId)) {
            return;
          }
          if (hasMoreOlder && onLoadOlder) {
            pendingScrollRestoreRef.current = null;
            if (isLoadingOlder) return;
            if (unreadSeekCountRef.current !== messages.length) {
              unreadSeekCountRef.current = messages.length;
              onLoadOlder();
              return;
            }
          }
        }
        initialScrollDoneRef.current = true;
      }

      if (pendingScrollRestoreRef.current != null) {
        const previousHeight = pendingScrollRestoreRef.current;
        body.scrollTop = body.scrollHeight - previousHeight;
        pendingScrollRestoreRef.current = null;
        return;
      }

      if (stickToBottomRef.current) {
        body.scrollTop = body.scrollHeight;
        requestAnimationFrame(focusInput);
      }
    }, [
      conversationId,
      messages,
      isLoading,
      isSendingMessage,
      inputDisabledForMember,
      isAnnouncement,
      firstUnreadMessageId,
      hasMoreOlder,
      isLoadingOlder,
      onLoadOlder,
    ]);

    const handleScroll = useCallback(() => {
      const body = chatBodyRef.current;
      if (!body) return;

      const distanceFromBottom =
        body.scrollHeight - body.scrollTop - body.clientHeight;
      stickToBottomRef.current = distanceFromBottom < 40;

      if (body.scrollTop <= LOAD_OLDER_SCROLL_THRESHOLD_PX) {
        requestOlderMessages();
      }
    }, [requestOlderMessages]);

    const handleSubmitMessage = useCallback(
      async (message: string) => {
        stickToBottomRef.current = true;
        await onSubmitMessage(message);
      },
      [onSubmitMessage]
    );

    return (
      <Stack
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <ChatHeader
          worker={headerWorker}
          onViewProfile={onViewProfile}
          showWorkerInfo={showWorkerInfo}
          isDirectMessage={isDirectMessage}
          isAnnouncement={isAnnouncement}
          onClose={onClose}
        />
        <hr className="mb-0" />
        <div
          ref={chatBodyRef}
          onScroll={handleScroll}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {!isAnnouncement && policyNotice}
          {isLoading && !error && <ThLoading />}
          {isLoadingOlder && (
            <div
              className="d-flex justify-content-center py-2"
              data-testid="load-older-messages-loader"
            >
              <ThLoading />
            </div>
          )}
          {/* Keep an already-loaded transcript visible: a failed older-page or
              send request must not blank the conversation. */}
          {messages.length > 0 && !isLoading && (
            <ChatBody
              chatMessages={messages}
              chatMembers={chatMembers}
              resendMessageMutation={resendMessageMutation}
              firstUnreadMessageId={firstUnreadMessageId}
            />
          )}
          {error && !isLoading && (
            <div data-testid="error-message">Error loading messages</div>
          )}
        </div>
        {!isAnnouncement && (
          <ChatInput
            ref={inputRef}
            onSubmitMessage={handleSubmitMessage}
            disabled={isSendingMessage}
            inputDisabledForMember={inputDisabledForMember}
            isSendingMessage={isSendingMessage}
            placeholder={
              isSendingMessage
                ? 'Sending message...'
                : 'Enter your message here...'
            }
          />
        )}
      </Stack>
    );
  },
  arePropsEqual
);

GenericChat.displayName = 'GenericChat';

export default GenericChat;
